import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import Spinner from './Spinner';
import styles from '../styles/ErrorPage.module.css';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  routeName?: string;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
  reloadAttempted: boolean;
}
const CHUNK_ERROR_PATTERNS = [
  'Loading chunk',
  'Failed to fetch dynamically imported module',
  "Unexpected token '<'",
] as const;

const CHUNK_LOAD_ERROR = 'ChunkLoadError';
class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      isChunkError: false,
      reloadAttempted: false,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<ChunkErrorBoundaryState> {
    const isChunkError =
      error.name === CHUNK_LOAD_ERROR ||
      CHUNK_ERROR_PATTERNS.some((pattern) => error.message?.includes(pattern));

    return {
      hasError: true,
      isChunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ChunkErrorBoundary caught error', {
      route: this.props.routeName,
      message: error.message,
      stack: errorInfo.componentStack,
    });

    if (!this.state.isChunkError) return;

    const key = `chunkReload_${this.props.routeName || 'default'}`;
    const attempted = sessionStorage.getItem(key);

    if (!attempted) {
      sessionStorage.setItem(key, 'true');

      setTimeout(() => {
        window.location.reload();
      }, 100);

      return;
    }

    this.setState({ reloadAttempted: true });
  }

  private handleRetry = (): void => {
    const key = `chunkReload_${this.props.routeName || 'default'}`;
    sessionStorage.removeItem(key);

    const url = new URL(window.location.href);
    url.searchParams.set('reload', Date.now().toString());

    window.location.href = url.toString();
  };
  render(): ReactNode {
    const { hasError, isChunkError, reloadAttempted } = this.state;

    if (hasError && isChunkError) {
      if (!reloadAttempted) {
        return <Spinner>Updating to latest version...</Spinner>;
      }

      return (
        <Box
          className={styles.page}
          component='section'
        >
          <Box
            className={styles.container}
            component='article'
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Typography
              className={styles.title}
              variant='h1'
            >
              Unable to load this page
            </Typography>
            <Typography
              className={styles.message}
              variant='body1'
            >
              The application may have been updated while this page was open.
              Please reload to continue using the latest version.
            </Typography>

            <Button
              className={styles.button}
              sx={{ mt: 4 }}
              onClick={this.handleRetry}
            >
              Reload Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
